import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import { ProdutoOportunidade } from "../../../../types/orcamento";
import { apiService } from "../../../../services/api";
import { ItemAdicaoMultiplaEstoque, AdicionarMultiplosEstoqueResponse } from "../../../../types/estoque";

interface GestaoEstoqueContratoProps {
    oportunidadeId: number;
    contratoId: number;
    formatCurrency: (value: number | string) => string;
}

interface EstoqueProduto extends ProdutoOportunidade {
    estoque_atual?: number;
}

interface AdicionarEstoqueFormData {
    id_produto: number;
    quantidade: number;
    custo_unitario: number;
    motivo: string;
    observacoes: string;
}

interface UploadedFile {
    file: File;
    size: string;
    name: string;
}

interface PrazoPagamento {
    id?: number;
    titulo: string;
    periodo_dias: number;
    quantidade_parcelas: number;
}

interface DadosContaPagar {
    prazo_pagamento: PrazoPagamento;
    categoria_id: number;
    fornecedor_id: number;
    descricao: string;
    observacoes: string;
}

const GestaoEstoqueContrato: React.FC<GestaoEstoqueContratoProps> = ({
    oportunidadeId,
    contratoId,
    formatCurrency
}) => {
    const [produtos, setProdutos] = useState<EstoqueProduto[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedProduto, setSelectedProduto] = useState<EstoqueProduto | null>(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Estados para o histórico de estoque
    const [historicoEstoque, setHistoricoEstoque] = useState<any[]>([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [showHistoricoModal, setShowHistoricoModal] = useState(false);
    const [produtoHistorico, setProdutoHistorico] = useState<EstoqueProduto | null>(null);

    // Dados do formulário
    const [formData, setFormData] = useState<AdicionarEstoqueFormData>({
        id_produto: 0,
        quantidade: 0,
        custo_unitario: 0,
        motivo: "",
        observacoes: ""
    });

    // Estados para anexos
    const [anexoData, setAnexoData] = useState({
        titulo: '',
        descricao: ''
    });
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Estados para segundo modal - prazo de pagamento
    const [showPrazoPagamentoModal, setShowPrazoPagamentoModal] = useState(false);
    const [prazosDisponiveis, setPrazosDisponiveis] = useState<PrazoPagamento[]>([]);
    const [dadosContaPagar, setDadosContaPagar] = useState<DadosContaPagar>({
        prazo_pagamento: { titulo: "", periodo_dias: 0, quantidade_parcelas: 1 },
        categoria_id: 0,
        fornecedor_id: 0,
        descricao: "",
        observacoes: ""
    });
    const [categorias, setCategorias] = useState<{ value: number, label: string }[]>([]);
    const [fornecedores, setFornecedores] = useState<{ value: number, label: string }[]>([]);
    
    // Estados para preservar dados do estoque para o segundo modal
    const [dadosEstoqueParaPagamento, setDadosEstoqueParaPagamento] = useState({
        produto: null as EstoqueProduto | null,
        quantidade: 0,
        custo_unitario: 0,
        motivo: "",
        observacoes: ""
    });

    // Estados para modal de adição múltipla de produtos
    const [showMultiAddModal, setShowMultiAddModal] = useState(false);
    const [itensMultiAdd, setItensMultiAdd] = useState<ItemAdicaoMultiplaEstoque[]>([]);
    const [multiAddFormData, setMultiAddFormData] = useState({
        motivo: "",
        observacoes: ""
    });
    const [itensAdicionadosSucesso, setItensAdicionadosSucesso] = useState<AdicionarMultiplosEstoqueResponse['data'] | null>(null);

    // Opções de motivo
    const motivosEstoque = [
        "Entrega de contrato",
        "Compra para estoque",
        "Transferência entre contratos",
        "Ajuste de inventário",
        "Entrada manual"
    ];

    // Função para formatar números (quantidades)
    const formatQuantity = (value: string | number) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue) || numValue === null || numValue === undefined) {
            return '0';
        }
        if (Number.isInteger(numValue)) {
            return numValue.toString();
        } else {
            return Number(numValue).toFixed(2).replace('.', ',');
        }
    };

    // Função para formatar estoque (com decimais)
    const formatEstoque = (value: number) => {
        const numValue = Number(value) || 0;
        if (isNaN(numValue)) {
            return '0';
        }
        if (Number.isInteger(numValue)) {
            return numValue.toString();
        } else {
            return numValue.toFixed(2).replace('.', ',');
        }
    };

    // Função auxiliar para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Manipuladores de arquivo para anexo
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files).map(file => ({
                file: file,
                size: formatFileSize(file.size),
                name: file.name
            }));
            setUploadedFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Limpar dados do modal de anexo
    const clearAnexoData = () => {
        setAnexoData({
            titulo: '',
            descricao: ''
        });
        setUploadedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Função para obter ícone do estoque
    const getEstoqueIcon = (estoque: number) => {
        if (estoque === 0) {
            return <i className="ti ti-alert-circle text-danger" title="Estoque zerado"></i>;
        } else if (estoque <= 10) {
            return <i className="ti ti-alert-triangle text-warning" title="Estoque baixo"></i>;
        } else {
            return <i className="ti ti-check-circle text-success" title="Estoque adequado"></i>;
        }
    };

    // Carregar produtos da oportunidade com informações de estoque
    const loadProdutos = async () => {
        try {
            setLoading(true);

            // Buscar produtos da oportunidade
            const produtosRes = await apiService.getProdutosOportunidade(oportunidadeId);

            if (produtosRes.success) {
                // Buscar estoque de todos os produtos em 1 chamada batch
                const ids = produtosRes.data.map(p => p.id_produto);
                const estoqueMap: Record<number, number> = {};
                try {
                    const batchRes = await apiService.getProdutosByIds(ids);
                    if (batchRes.success && Array.isArray(batchRes.data)) {
                        batchRes.data.forEach(p => { estoqueMap[p.id] = p.estoque || 0; });
                    }
                } catch (error) {
                    console.error('Erro ao buscar estoques dos produtos:', error);
                }

                const produtosComEstoque = produtosRes.data.map(produto => ({
                    ...produto,
                    estoque_atual: estoqueMap[produto.id_produto] ?? 0
                }));

                setProdutos(produtosComEstoque);
            }

        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
            toast.error("Erro ao carregar produtos do contrato");
        } finally {
            setLoading(false);
        }
    };

    // Buscar histórico de estoque de um produto específico do contrato
    const buscarHistoricoEstoque = async (produto: EstoqueProduto) => {
        try {
            setLoadingHistorico(true);
            setProdutoHistorico(produto);
            
            // Buscar histórico de estoque do contrato para este produto
            const response = await apiService.getHistoricoEstoqueContrato(contratoId);
            
            if (response.success) {
                // Filtrar apenas as movimentações deste produto
                const movimentacoesProduto = response.data.filter((mov: any) => mov.id_produto === produto.id_produto);
                setHistoricoEstoque(movimentacoesProduto);
            }
            
            setShowHistoricoModal(true);
        } catch (error) {
            console.error("Erro ao buscar histórico de estoque:", error);
            toast.error("Erro ao carregar histórico de estoque");
        } finally {
            setLoadingHistorico(false);
        }
    };

    // Função para formatar data
    const formatDate = (dateString: string): string => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    // Buscar dados iniciais
    const fetchDadosIniciais = async () => {
        try {
            // Buscar categorias financeiras
            const categoriasResponse = await apiService.getCategoriaFinanceira();
            if (categoriasResponse.success && categoriasResponse.data) {
                let categoriasArray: any[] = [];
                if (Array.isArray(categoriasResponse.data)) {
                    categoriasArray = categoriasResponse.data;
                } else {
                    const responseData = categoriasResponse.data as any;
                    categoriasArray = responseData.data || [];
                }

                const categoriasDespesa = categoriasArray
                    .filter(categoria => categoria.tipo === 'DESPESA')
                    .map(categoria => ({
                        value: categoria.id,
                        label: categoria.nome
                    }));
                setCategorias(categoriasDespesa);
            }

            // Buscar fornecedores
            const fornecedoresResponse = await apiService.getFornecedores();
            if (fornecedoresResponse.success && fornecedoresResponse.data) {
                const fornecedoresOptions = fornecedoresResponse.data.map(fornecedor => ({
                    value: fornecedor.id,
                    label: fornecedor.nome
                }));
                setFornecedores(fornecedoresOptions);
            }

            // Prazos de pagamento padrão
            setPrazosDisponiveis([
                { id: 1, titulo: "À Vista", periodo_dias: 0, quantidade_parcelas: 1 },
                { id: 2, titulo: "30 dias", periodo_dias: 30, quantidade_parcelas: 1 },
                { id: 3, titulo: "60 dias", periodo_dias: 60, quantidade_parcelas: 1 },
                { id: 4, titulo: "2x (30/60)", periodo_dias: 30, quantidade_parcelas: 2 },
                { id: 5, titulo: "3x (30/60/90)", periodo_dias: 30, quantidade_parcelas: 3 },
            ]);
        } catch (error) {
            console.error('Erro ao buscar dados iniciais:', error);
        }
    };

    // Carregar dados ao montar o componente
    useEffect(() => {
        if (oportunidadeId) {
            loadProdutos();
            fetchDadosIniciais();
        }
    }, [oportunidadeId]);

    // Abrir modal para adicionar estoque
    const handleAdicionarEstoque = (produto: EstoqueProduto) => {
        setSelectedProduto(produto);
        setFormData({
            id_produto: produto.id_produto,
            quantidade: 0,
            custo_unitario: 0,
            motivo: "",
            observacoes: ""
        });
        setShowModal(true);
    };

    // Fechar modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProduto(null);
        setFormData({
            id_produto: 0,
            quantidade: 0,
            custo_unitario: 0,
            motivo: "",
            observacoes: ""
        });
        clearAnexoData();
    };

    // Manipular mudanças no formulário
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        console.log(`Campo alterado: ${name}, Valor: ${value}`);
        
        if (name === 'quantidade' || name === 'custo_unitario') {
            const numericValue = parseFloat(value) || 0;
            console.log(`Valor numérico convertido para ${name}:`, numericValue);
            
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        // Log do estado atualizado
        console.log('FormData atualizado:', { ...formData, [name]: (name === 'quantidade' || name === 'custo_unitario') ? (parseFloat(value) || 0) : value });
    };

    // Submeter formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validações
        if (formData.quantidade <= 0) {
            toast.error("A quantidade deve ser maior que zero");
            return;
        }

        if (formData.custo_unitario <= 0) {
            toast.error("O custo unitário deve ser maior que zero");
            return;
        }

        if (!formData.motivo) {
            toast.error("O motivo é obrigatório");
            return;
        }

        try {
            setSubmitting(true);

            // Log dos dados que serão enviados
            console.log('Dados do formulário antes do envio:', {
                id_contrato: contratoId,
                id_produto: formData.id_produto,
                quantidade: formData.quantidade,
                custo_unitario: formData.custo_unitario,
                motivo: formData.motivo,
                observacoes: formData.observacoes
            });

            // Adicionar estoque via contrato
            const response = await apiService.adicionarEstoqueViaContrato({
                id_contrato: contratoId,
                id_produto: formData.id_produto,
                quantidade: formData.quantidade,
                custo_unitario: formData.custo_unitario,
                motivo: formData.motivo,
                observacoes: formData.observacoes
            });

            console.log('Resposta da API:', response);

            if (response.success) {
                // Log da estrutura da resposta para debug
                console.log('Estrutura da resposta:', JSON.stringify(response, null, 2));
                
                // Se houver anexos, anexá-los após adicionar o estoque
                if (uploadedFiles.length > 0 && anexoData.titulo.trim()) {
                    console.log('Tentando anexar arquivo...');
                    
                    // Verificar se temos o ID do histórico (ajustado para id_historico)
                    if (response.data && (response.data.id_historico)) {
                        try {
                            const historicoId = response.data.id_historico;
                            console.log('Anexando com ID histórico:', historicoId);
                            await apiService.createAnexoHistoricoEstoque({
                                id_historico_estoque: parseInt(historicoId.toString()),
                                titulo: anexoData.titulo,
                                descricao: anexoData.descricao,
                                arquivo: uploadedFiles[0].file
                            });
                            console.log('Anexo criado com sucesso');
                            toast.success('Anexo adicionado com sucesso!');
                        } catch (anexoError) {
                            console.error('Erro ao anexar documento:', anexoError);
                            toast.warning('Estoque adicionado, mas houve erro ao anexar o documento');
                        }
                    } else {
                        console.warn('ID do histórico de estoque não encontrado na resposta:', response);
                        
                        // Solução temporária: tentar buscar o último histórico do produto e contrato
                        try {
                            console.log('Tentando buscar último histórico de estoque...');
                            const historicoResponse = await apiService.getHistoricoEstoqueContrato(contratoId);
                            
                            if (historicoResponse.success && historicoResponse.data && historicoResponse.data.length > 0) {
                                // Buscar o histórico mais recente deste produto
                                const ultimoHistorico = historicoResponse.data
                                    .filter((h: any) => h.id_produto === formData.id_produto)
                                    .sort((a: any, b: any) => new Date(b.data_movimento).getTime() - new Date(a.data_movimento).getTime())[0];
                                
                                if (ultimoHistorico && ultimoHistorico.id) {
                                    console.log('Anexando com último histórico encontrado:', ultimoHistorico.id);
                                    await apiService.createAnexoHistoricoEstoque({
                                        id_historico_estoque: ultimoHistorico.id,
                                        titulo: anexoData.titulo,
                                        descricao: anexoData.descricao,
                                        arquivo: uploadedFiles[0].file
                                    });
                                    console.log('Anexo criado com sucesso usando último histórico');
                                    toast.success('Anexo adicionado com sucesso!');
                                } else {
                                    toast.warning('Estoque adicionado, mas não foi possível anexar o documento (histórico não encontrado)');
                                }
                            } else {
                                toast.warning('Estoque adicionado, mas não foi possível anexar o documento (erro ao buscar histórico)');
                            }
                        } catch (historicoError) {
                            console.error('Erro ao buscar histórico para anexar:', historicoError);
                            toast.warning('Estoque adicionado, mas não foi possível anexar o documento');
                        }
                    }
                }

                toast.success("Estoque adicionado com sucesso!");
                
                // Abrir modal de prazo de pagamento se o valor total for significativo
                const valorTotal = formData.quantidade * formData.custo_unitario;
                if (valorTotal > 0) {
                    // Preservar dados do estoque para o segundo modal
                    setDadosEstoqueParaPagamento({
                        produto: selectedProduto,
                        quantidade: formData.quantidade,
                        custo_unitario: formData.custo_unitario,
                        motivo: formData.motivo,
                        observacoes: formData.observacoes
                    });
                    
                    // Preparar dados para o contas a pagar
                    setDadosContaPagar({
                        ...dadosContaPagar,
                        descricao: `Estoque ${selectedProduto?.nome_produto} - ${formData.motivo}`,
                        observacoes: formData.observacoes
                    });
                    
                    console.log('Dados preservados para o modal de pagamento:', {
                        produto: selectedProduto?.nome_produto,
                        quantidade: formData.quantidade,
                        custo_unitario: formData.custo_unitario,
                        valor_total: valorTotal
                    });
                    
                    // Fechar modal atual e abrir modal de prazo
                    handleCloseModal();
                    setShowPrazoPagamentoModal(true);
                } else {
                    handleCloseModal();
                    loadProdutos(); // Recarregar dados
                }
            } else {
                toast.error(response.message || "Erro ao adicionar estoque");
            }

        } catch (error) {
            console.error("Erro ao adicionar estoque:", error);
            toast.error("Erro ao adicionar estoque");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-2">Carregando gestão de estoque...</p>
            </div>
        );
    }

    // Função para fechar modal de histórico
    const fecharHistoricoModal = () => {
        setShowHistoricoModal(false);
        setProdutoHistorico(null);
        setHistoricoEstoque([]);
    };

    // ========================================================================
    // FUNÇÕES PARA MODAL DE ADIÇÃO MÚLTIPLA
    // ========================================================================

    // Abrir modal de adição múltipla
    const handleAbrirMultiAddModal = () => {
        setItensMultiAdd([]);
        setMultiAddFormData({
            motivo: "",
            observacoes: ""
        });
        setShowMultiAddModal(true);
    };

    // Fechar modal de adição múltipla
    const handleCloseMultiAddModal = () => {
        setShowMultiAddModal(false);
        setItensMultiAdd([]);
        setMultiAddFormData({
            motivo: "",
            observacoes: ""
        });
    };

    // Adicionar item à lista
    const handleAdicionarItemLista = () => {
        if (produtos.length === 0) {
            toast.error("Não há produtos disponíveis");
            return;
        }

        // Verificar se já adicionou todos os produtos disponíveis
        const produtosIdsUsados = itensMultiAdd.map(item => item.id_produto);
        const produtosDisponiveis = produtos.filter(p => !produtosIdsUsados.includes(p.id_produto));

        if (produtosDisponiveis.length === 0) {
            toast.error("Todos os produtos já foram adicionados");
            return;
        }

        const primeiroProdutoDisponivel = produtosDisponiveis[0];

        setItensMultiAdd(prev => [
            ...prev,
            {
                id_produto: primeiroProdutoDisponivel.id_produto,
                quantidade: 1,
                custo_unitario: 0
            }
        ]);
    };

    // Remover item da lista
    const handleRemoverItemLista = (index: number) => {
        setItensMultiAdd(prev => prev.filter((_, i) => i !== index));
    };

    // Alterar item da lista
    const handleAlterarItemLista = (index: number, campo: keyof ItemAdicaoMultiplaEstoque, valor: any) => {
        setItensMultiAdd(prev => {
            const novosItens = [...prev];
            const itemAtual = { ...novosItens[index] };

            if (campo === 'id_produto') {
                const produtoSelecionado = produtos.find(p => p.id_produto === parseInt(valor));
                if (produtoSelecionado) {
                    itemAtual.id_produto = produtoSelecionado.id_produto;
                    itemAtual.quantidade = itemAtual.quantidade || 1;
                    itemAtual.custo_unitario = itemAtual.custo_unitario || 0;
                }
            } else if (campo === 'quantidade' || campo === 'custo_unitario') {
                itemAtual[campo] = parseFloat(valor) || 0;
            }

            novosItens[index] = itemAtual;
            return novosItens;
        });
    };

    // Calcular subtotal de um item
    const calcularSubtotalItem = (item: ItemAdicaoMultiplaEstoque) => {
        return (item.quantidade || 0) * (item.custo_unitario || 0);
    };

    // Calcular total da compra
    const calcularTotalCompra = () => {
        return itensMultiAdd.reduce((total, item) => total + calcularSubtotalItem(item), 0);
    };

    // Buscar informações do produto pelo ID
    const getProdutoById = (id: number) => {
        return produtos.find(p => p.id_produto === id);
    };

    // Submeter formulário de adição múltipla
    const handleSubmitMultiAdd = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validações
        if (itensMultiAdd.length === 0) {
            toast.error("Adicione pelo menos um produto");
            return;
        }

        if (!multiAddFormData.motivo) {
            toast.error("O motivo é obrigatório");
            return;
        }

        // Validar itens
        for (const item of itensMultiAdd) {
            if (!item.quantidade || item.quantidade <= 0) {
                toast.error("Todos os produtos devem ter quantidade maior que zero");
                return;
            }
            if (!item.custo_unitario || item.custo_unitario <= 0) {
                toast.error("Todos os produtos devem ter custo unitário maior que zero");
                return;
            }
        }

        try {
            setSubmitting(true);

            const response = await apiService.adicionarMultiplosEstoqueViaContrato({
                id_contrato: contratoId,
                motivo: multiAddFormData.motivo,
                observacoes: multiAddFormData.observacoes,
                itens: itensMultiAdd
            });

            if (response.success) {
                toast.success(`Estoque adicionado com sucesso para ${response.data.quantidade_itens} produto(s)!`);

                // Preservar dados para o modal de pagamento
                setItensAdicionadosSucesso(response.data);

                // Preparar descrição sucinta
                const descricao = `Entrada de estoque - ${response.data.quantidade_itens} produto(s)`;

                // Preparar observações detalhadas
                const observacoesDetalhadas = response.data.itens_adicionados.map(item => {
                    const produto = getProdutoById(item.id_produto);
                    return `- ${produto?.nome_produto || `Produto ${item.id_produto}`}: ${item.quantidade} x R$ ${item.custo_unitario.toFixed(2)} = R$ ${item.valor_total.toFixed(2)}`;
                }).join('\n') + `\n\nTotal: R$ ${response.data.valor_total.toFixed(2)}\nMotivo: ${multiAddFormData.motivo}`;

                // Preparar dados para o contas a pagar
                setDadosContaPagar({
                    ...dadosContaPagar,
                    descricao: descricao,
                    observacoes: observacoesDetalhadas
                });

                // Fechar modal de estoque e abrir modal de prazo
                handleCloseMultiAddModal();
                setShowPrazoPagamentoModal(true);
            }

        } catch (error) {
            console.error("Erro ao adicionar múltiplos produtos:", error);
            toast.error(error instanceof Error ? error.message : "Erro ao adicionar múltiplos produtos");
        } finally {
            setSubmitting(false);
        }
    };

    // ========================================================================
    // FUNÇÕES PARA O SEGUNDO MODAL - PRAZO DE PAGAMENTO
    // ========================================================================

    // Funções para o segundo modal - prazo de pagamento
    const handleClosePrazoPagamentoModal = () => {
        setShowPrazoPagamentoModal(false);
        setDadosContaPagar({
            prazo_pagamento: { titulo: "", periodo_dias: 0, quantidade_parcelas: 1 },
            categoria_id: 0,
            fornecedor_id: 0,
            descricao: "",
            observacoes: ""
        });
        loadProdutos(); // Recarregar dados
    };

    const handleConfirmarContaPagar = async () => {
        try {
            if (!dadosContaPagar.categoria_id || !dadosContaPagar.fornecedor_id || !dadosContaPagar.prazo_pagamento.titulo) {
                toast.error("Preencha todos os campos obrigatórios");
                return;
            }

            // Verificar se é adição múltipla ou única
            const isMultipla = itensAdicionadosSucesso !== null;
            const valorTotal = isMultipla
                ? itensAdicionadosSucesso!.valor_total
                : dadosEstoqueParaPagamento.quantidade * dadosEstoqueParaPagamento.custo_unitario;

            const valorPorParcela = valorTotal / dadosContaPagar.prazo_pagamento.quantidade_parcelas;

            console.log('Dados para criação de contas a pagar:', {
                valorTotal,
                valorPorParcela,
                quantidade_parcelas: dadosContaPagar.prazo_pagamento.quantidade_parcelas,
                fornecedor: dadosContaPagar.fornecedor_id,
                categoria: dadosContaPagar.categoria_id,
                isMultipla
            });

            // Validações adicionais
            if (valorPorParcela <= 0) {
                toast.error("Valor por parcela deve ser maior que zero");
                return;
            }

            // Criar as contas a pagar com base no prazo selecionado
            for (let i = 0; i < dadosContaPagar.prazo_pagamento.quantidade_parcelas; i++) {
                const dataVencimento = new Date();
                dataVencimento.setDate(dataVencimento.getDate() + (dadosContaPagar.prazo_pagamento.periodo_dias * (i + 1)));

                const contaPagar = {
                    id_fornecedor: dadosContaPagar.fornecedor_id,
                    id_categoria: dadosContaPagar.categoria_id,
                    id_contrato: contratoId, // VINCULANDO O CONTRATO
                    descricao: `${dadosContaPagar.descricao} - Parcela ${i + 1}/${dadosContaPagar.prazo_pagamento.quantidade_parcelas}`,
                    valor_original: Number(valorPorParcela.toFixed(2)),
                    data_emissao: new Date().toISOString().split('T')[0],
                    data_vencimento: dataVencimento.toISOString().split('T')[0],
                    parcela_atual: i + 1,
                    total_parcelas: dadosContaPagar.prazo_pagamento.quantidade_parcelas,
                    observacoes: dadosContaPagar.observacoes || ""
                };

                console.log(`Dados detalhados da conta a pagar ${i + 1}:`, {
                    ...contaPagar,
                    validacoes: {
                        id_fornecedor_valido: !!contaPagar.id_fornecedor && contaPagar.id_fornecedor > 0,
                        id_categoria_valido: !!contaPagar.id_categoria && contaPagar.id_categoria > 0,
                        id_contrato_valido: !!contaPagar.id_contrato && contaPagar.id_contrato > 0,
                        descricao_valida: !!contaPagar.descricao && contaPagar.descricao.trim().length > 0,
                        valor_valido: !!contaPagar.valor_original && contaPagar.valor_original > 0,
                        data_emissao_valida: !!contaPagar.data_emissao,
                        data_vencimento_valida: !!contaPagar.data_vencimento
                    }
                });

                try {
                    const response = await apiService.createContaPagar(contaPagar);
                    console.log(`Conta a pagar ${i + 1} criada com sucesso:`, response);
                } catch (parcelaError: any) {
                    console.error(`Erro detalhado ao criar parcela ${i + 1}:`, {
                        error: parcelaError,
                        message: parcelaError?.message,
                        stack: parcelaError?.stack,
                        dadosEnviados: contaPagar
                    });

                    // Extrair mensagem de erro mais específica
                    let errorMessage = `Erro ao criar parcela ${i + 1}`;
                    if (parcelaError?.response?.data?.message) {
                        errorMessage += `: ${parcelaError.response.data.message}`;
                    } else if (parcelaError?.message) {
                        errorMessage += `: ${parcelaError.message}`;
                    }

                    throw new Error(errorMessage);
                }
            }

            toast.success(`${dadosContaPagar.prazo_pagamento.quantidade_parcelas} conta(s) a pagar criada(s) com sucesso!`);
            handleClosePrazoPagamentoModal();

            // Limpar dados de múltiplos itens
            setItensAdicionadosSucesso(null);

        } catch (error) {
            console.error('Erro ao criar contas a pagar:', error);

            // Mostrar erro mais específico
            const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido ao criar contas a pagar';
            toast.error(errorMessage);
        }
    };

    return (
        <>
            <style>{`
                .bg-primary-light {
                    background-color: rgba(13, 110, 253, 0.1) !important;
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .cursor-pointer:hover {
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                }
            `}</style>

            {/* Tabela de Produtos */}
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Gestão de Estoque dos Produtos</h5>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleAbrirMultiAddModal()}
                        disabled={produtos.length === 0}
                    >
                        <i className="ti ti-plus me-1"></i>
                        Adicionar Múltiplos Produtos
                    </button>
                </div>
                <div className="card-body">
                    {produtos.length === 0 ? (
                        <div className="alert alert-info">
                            Nenhum produto encontrado para este contrato.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th>Marca</th>
                                        <th>Descrição</th>
                                        <th>Estoque Atual</th>
                                        <th>Unidade</th>
                                        <th>Qtd. Contratada</th>
                                        <th>Histórico</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {produtos.map((produto) => (
                                        <tr key={produto.id}>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '200px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={produto.nome_produto}
                                                >
                                                    {produto.nome_produto}
                                                </div>
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '120px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={(produto as any).marca || 'Sem marca'}
                                                >
                                                    {(produto as any).marca || <span className="text-muted">-</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={(produto as any).descricao_especifica || 'Sem descrição específica'}
                                                >
                                                    {(produto as any).descricao_especifica || <span className="text-muted">-</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getEstoqueIcon(produto.estoque_atual || 0)}
                                                    <span className="ms-2">{formatEstoque(produto.estoque_atual || 0)}</span>
                                                </div>
                                            </td>
                                            <td>{produto.unidade_medida || "UN"}</td>
                                            <td>{formatQuantity(produto.quantidade)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-info"
                                                    onClick={() => buscarHistoricoEstoque(produto)}
                                                    title="Ver histórico de estoque"
                                                    disabled={loadingHistorico}
                                                >
                                                    <i className="ti ti-history"></i>
                                                    {loadingHistorico && (
                                                        <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true"></span>
                                                    )}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={() => handleAdicionarEstoque(produto)}
                                                    title="Adicionar estoque"
                                                >
                                                    <i className="ti ti-plus"></i> Adicionar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Histórico de Estoque */}
            <Modal show={showHistoricoModal} onHide={fecharHistoricoModal} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center">
                        <i className="ti ti-history text-info me-2"></i>
                        Histórico de Estoque
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {produtoHistorico && (
                        <>
                            {/* Header do Produto */}
                            <div className="bg-primary-light p-3 border-bottom">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <h5 className="mb-1 text-primary">{produtoHistorico.nome_produto}</h5>
                                        <div className="text-muted small">
                                            {((produtoHistorico as any).marca || (produtoHistorico as any).descricao_especifica) && (
                                                <>
                                                    {(produtoHistorico as any).marca && (
                                                        <span className="me-3"><i className="ti ti-tag me-1"></i>{(produtoHistorico as any).marca}</span>
                                                    )}
                                                    {(produtoHistorico as any).descricao_especifica && (
                                                        <span><i className="ti ti-info-circle me-1"></i>{(produtoHistorico as any).descricao_especifica}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-end">
                                        <div className="d-flex align-items-center justify-content-end">
                                            {getEstoqueIcon(produtoHistorico.estoque_atual || 0)}
                                            <span className="ms-2 fw-bold">{formatEstoque(produtoHistorico.estoque_atual || 0)} {produtoHistorico.unidade_medida || "UN"}</span>
                                        </div>
                                        <small className="text-muted">Estoque Atual</small>
                                    </div>
                                </div>
                            </div>

                            {/* Conteúdo do Histórico */}
                            <div className="p-3">
                                {loadingHistorico ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Carregando...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Carregando histórico...</p>
                                    </div>
                                ) : historicoEstoque.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Data</th>
                                                    <th>Quantidade</th>
                                                    <th>Custo Unit.</th>
                                                    <th>Valor Total</th>
                                                    <th>Motivo</th>
                                                    <th>Observações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historicoEstoque.map((mov: any, index: number) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <span className={`badge ${mov.tipo_movimento === 'Entrada' ? 'bg-success' : 'bg-warning'}`}>
                                                                <i className={`ti ${mov.tipo_movimento === 'Entrada' ? 'ti-arrow-up' : 'ti-arrow-down'} me-1`}></i>
                                                                {mov.tipo_movimento}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {formatDate(mov.data_movimento)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-success">
                                                                {formatEstoque(mov.quantidade)} {produtoHistorico.unidade_medida || "UN"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="text-primary fw-bold">
                                                                {formatCurrency(mov.custo_unitario || 0)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="text-success fw-bold">
                                                                {formatCurrency((mov.quantidade || 0) * (mov.custo_unitario || 0))}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="text-dark">{mov.motivo}</span>
                                                        </td>
                                                        <td>
                                                            {mov.observacoes ? (
                                                                <span className="text-muted small">{mov.observacoes}</span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="mb-3">
                                            <i className="ti ti-package-off display-4 text-muted"></i>
                                        </div>
                                        <h5 className="text-muted">Nenhuma Movimentação</h5>
                                        <p className="text-muted mb-0">
                                            Ainda não há movimentações de estoque para este produto neste contrato.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={fecharHistoricoModal}>
                        <i className="ti ti-x me-1"></i>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Adição Múltipla de Estoque */}
            <Modal show={showMultiAddModal} onHide={handleCloseMultiAddModal} size="lg" centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ti ti-package-plus text-primary me-2"></i>
                        Adicionar Múltiplos Produtos ao Estoque
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmitMultiAdd}>
                    <Modal.Body>
                        <div className="row">
                            {/* Motivo */}
                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Motivo <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={multiAddFormData.motivo}
                                        onChange={(e) => setMultiAddFormData({
                                            ...multiAddFormData,
                                            motivo: e.target.value
                                        })}
                                        required
                                    >
                                        <option value="">Selecione um motivo</option>
                                        {motivosEstoque.map((motivo, index) => (
                                            <option key={index} value={motivo}>
                                                {motivo}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Observações */}
                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="form-label">Observações</label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        value={multiAddFormData.observacoes}
                                        onChange={(e) => setMultiAddFormData({
                                            ...multiAddFormData,
                                            observacoes: e.target.value
                                        })}
                                        placeholder="Observações adicionais..."
                                    />
                                </div>
                            </div>

                            {/* Itens do Estoque */}
                            <div className="col-md-12">
                                <label className="form-label d-flex justify-content-between align-items-center mb-2">
                                    <span>Produtos <span className="text-danger">*</span></span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={handleAdicionarItemLista}
                                        disabled={submitting}
                                    >
                                        <i className="ti ti-plus me-1"></i>
                                        Adicionar Produto
                                    </button>
                                </label>

                                {itensMultiAdd.length === 0 && (
                                    <div className="alert alert-secondary text-center">
                                        Nenhum produto adicionado. Clique em "Adicionar Produto" para começar.
                                    </div>
                                )}

                                {itensMultiAdd.map((item, index) => {
                                    const produto = getProdutoById(item.id_produto);
                                    const produtosIdsUsados = itensMultiAdd.map(i => i.id_produto);
                                    const produtosDisponiveis = produtos.filter(p => !produtosIdsUsados.includes(p.id_produto) || p.id_produto === item.id_produto);

                                    return (
                                        <div key={index} className="card mb-2 border">
                                            <div className="card-body p-2">
                                                <div className="row align-items-center">
                                                    <div className="col-md-5 mb-2 mb-md-0">
                                                        <label className="form-label visually-hidden">Produto</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.id_produto}
                                                            onChange={(e) => handleAlterarItemLista(index, 'id_produto', e.target.value)}
                                                            required
                                                            disabled={submitting}
                                                        >
                                                            <option value="" disabled>Selecione...</option>
                                                            {produtosDisponiveis.map(prod => (
                                                                <option key={prod.id_produto} value={prod.id_produto}>
                                                                    {prod.nome_produto}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-2 col-6 mb-2 mb-md-0">
                                                        <label className="form-label visually-hidden">Quantidade</label>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={item.quantidade || ""}
                                                            onChange={(e) => handleAlterarItemLista(index, 'quantidade', e.target.value)}
                                                            step="0.01"
                                                            min="0.01"
                                                            placeholder="Qtde"
                                                            required
                                                            disabled={submitting}
                                                        />
                                                    </div>
                                                    <div className="col-md-3 col-6 mb-2 mb-md-0">
                                                        <label className="form-label visually-hidden">Custo Unitário</label>
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text">R$</span>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={item.custo_unitario || ""}
                                                                onChange={(e) => handleAlterarItemLista(index, 'custo_unitario', e.target.value)}
                                                                step="0.01"
                                                                min="0.01"
                                                                placeholder="0,00"
                                                                required
                                                                disabled={submitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 d-flex align-items-center justify-content-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleRemoverItemLista(index)}
                                                            disabled={submitting}
                                                        >
                                                            <i className="ti ti-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 text-end mt-1">
                                                        <small className="text-muted">
                                                            Estoque atual: {produto ? formatEstoque(produto.estoque_atual || 0) : '-'} {produto?.unidade_medida || "UN"}
                                                        </small>
                                                        <span className="mx-2">|</span>
                                                        <small className="fw-bold">
                                                            Subtotal: {formatCurrency(calcularSubtotalItem(item))}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Total da Compra */}
                            {itensMultiAdd.length > 0 && (
                                <div className="col-md-12">
                                    <div className="alert alert-info mb-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>Total da Compra:</strong> {formatCurrency(calcularTotalCompra())}
                                            </div>
                                            <div>
                                                <span className="badge bg-primary">{itensMultiAdd.length} produto(s)</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseMultiAddModal} disabled={submitting}>
                            <i className="ti ti-x me-1"></i>
                            Cancelar
                        </Button>
                        <Button
                            variant="success"
                            type="submit"
                            disabled={submitting || itensMultiAdd.length === 0}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Adicionando...
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-package me-1"></i>
                                    Adicionar Estoque
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Modal de Prazo de Pagamento */}
            <Modal show={showPrazoPagamentoModal} onHide={handleClosePrazoPagamentoModal} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center">
                        <i className="ti ti-calendar-dollar text-success me-2"></i>
                        Configurar Prazo de Pagamento
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="bg-primary-light p-3 border-bottom">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <h5 className="mb-1 text-primary">Detalhes da Compra</h5>
                                <div className="text-muted small">
                                    {itensAdicionadosSucesso ? (
                                        // Adição múltipla de produtos
                                        <>
                                            <span className="me-3">
                                                <i className="ti ti-package me-1"></i>
                                                {itensAdicionadosSucesso.quantidade_itens} produto(s)
                                            </span>
                                            <span className="me-3">
                                                <i className="ti ti-list me-1"></i>
                                                {itensAdicionadosSucesso.itens_adicionados.length} item(ns)
                                            </span>
                                        </>
                                    ) : (
                                        // Adição de produto único
                                        <>
                                            <span className="me-3">
                                                <i className="ti ti-package me-1"></i>
                                                {dadosEstoqueParaPagamento.produto?.nome_produto}
                                            </span>
                                            <span className="me-3">
                                                <i className="ti ti-calculator me-1"></i>
                                                {formatQuantity(dadosEstoqueParaPagamento.quantidade)} {dadosEstoqueParaPagamento.produto?.unidade_medida || "UN"}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-4 text-end">
                                <div className="fw-bold fs-5 text-success">
                                    {formatCurrency(
                                        itensAdicionadosSucesso?.valor_total
                                            ?? dadosEstoqueParaPagamento.quantidade * dadosEstoqueParaPagamento.custo_unitario
                                    )}
                                </div>
                                <small className="text-muted">Valor Total</small>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        <form>
                            <div className="row">
                                {/* Prazo de Pagamento */}
                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Prazo de Pagamento <span className="text-danger">*</span>
                                        </label>
                                        <div className="row">
                                            {prazosDisponiveis.map((prazo) => (
                                                <div key={prazo.id} className="col-md-6 mb-2">
                                                    <div 
                                                        className={`card cursor-pointer ${dadosContaPagar.prazo_pagamento.titulo === prazo.titulo ? 'border-primary bg-primary-light' : 'border-light'}`}
                                                        onClick={() => setDadosContaPagar({
                                                            ...dadosContaPagar,
                                                            prazo_pagamento: prazo
                                                        })}
                                                    >
                                                        <div className="card-body py-2 px-3">
                                                            <div className="d-flex align-items-center">
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-semibold">{prazo.titulo}</div>
                                                                    <small className="text-muted">
                                                                        {prazo.quantidade_parcelas > 1 ? `${prazo.quantidade_parcelas} parcelas` : 'À vista'}
                                                                    </small>
                                                                </div>
                                                                {dadosContaPagar.prazo_pagamento.titulo === prazo.titulo && (
                                                                    <i className="ti ti-check-circle text-primary"></i>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Fornecedor */}
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Fornecedor <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={dadosContaPagar.fornecedor_id}
                                            onChange={(e) => setDadosContaPagar({
                                                ...dadosContaPagar,
                                                fornecedor_id: parseInt(e.target.value)
                                            })}
                                            required
                                        >
                                            <option value={0}>Selecione o fornecedor</option>
                                            {fornecedores.map((fornecedor) => (
                                                <option key={fornecedor.value} value={fornecedor.value}>
                                                    {fornecedor.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Categoria */}
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Categoria <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={dadosContaPagar.categoria_id}
                                            onChange={(e) => setDadosContaPagar({
                                                ...dadosContaPagar,
                                                categoria_id: parseInt(e.target.value)
                                            })}
                                            required
                                        >
                                            <option value={0}>Selecione a categoria</option>
                                            {categorias.map((categoria) => (
                                                <option key={categoria.value} value={categoria.value}>
                                                    {categoria.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Descrição */}
                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Descrição
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={dadosContaPagar.descricao}
                                            onChange={(e) => setDadosContaPagar({
                                                ...dadosContaPagar,
                                                descricao: e.target.value
                                            })}
                                            placeholder="Descrição da conta a pagar"
                                        />
                                    </div>
                                </div>

                                {/* Observações */}
                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Observações
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={dadosContaPagar.observacoes}
                                            onChange={(e) => setDadosContaPagar({
                                                ...dadosContaPagar,
                                                observacoes: e.target.value
                                            })}
                                            placeholder="Observações adicionais"
                                        />
                                    </div>
                                </div>

                                {/* Preview das parcelas */}
                                {dadosContaPagar.prazo_pagamento.quantidade_parcelas > 1 && (
                                    <div className="col-md-12">
                                        <div className="alert alert-info">
                                            <h6 className="mb-2">
                                                <i className="ti ti-info-circle me-1"></i>
                                                Parcelas que serão criadas:
                                            </h6>
                                            {(() => {
                                                const valorTotal = itensAdicionadosSucesso?.valor_total
                                                    ?? dadosEstoqueParaPagamento.quantidade * dadosEstoqueParaPagamento.custo_unitario;

                                                return Array.from({ length: dadosContaPagar.prazo_pagamento.quantidade_parcelas }, (_, index) => {
                                                    const dataVencimento = new Date();
                                                    dataVencimento.setDate(dataVencimento.getDate() + (dadosContaPagar.prazo_pagamento.periodo_dias * (index + 1)));
                                                    const valorParcela = valorTotal / dadosContaPagar.prazo_pagamento.quantidade_parcelas;

                                                    return (
                                                        <div key={index} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                                            <span>Parcela {index + 1}/{dadosContaPagar.prazo_pagamento.quantidade_parcelas}</span>
                                                            <span>{formatCurrency(valorParcela)}</span>
                                                            <span className="text-muted small">{dataVencimento.toLocaleDateString('pt-BR')}</span>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={handleClosePrazoPagamentoModal}>
                        <i className="ti ti-x me-1"></i>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={handleConfirmarContaPagar}>
                        <i className="ti ti-check me-1"></i>
                        Confirmar e Criar Contas a Pagar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Adicionar Estoque */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Estoque via Contrato</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {selectedProduto && (
                            <>
                                {/* Informações do Produto */}
                                <div className="mb-3">
                                    <label className="form-label">Produto</label>
                                    <div className="form-control bg-light">
                                        {selectedProduto.nome_produto}
                                    </div>
                                </div>

                                {/* Especificação */}
                                <div className="mb-3">
                                    <label className="form-label">Especificação</label>
                                    <div className="form-control bg-light">
                                        {((selectedProduto as any).marca || (selectedProduto as any).descricao_especifica) ? (
                                            <>
                                                {(selectedProduto as any).marca && (
                                                    <span><strong>Marca:</strong> {(selectedProduto as any).marca}</span>
                                                )}
                                                {(selectedProduto as any).marca && (selectedProduto as any).descricao_especifica && <br />}
                                                {(selectedProduto as any).descricao_especifica && (
                                                    <span><strong>Descrição:</strong> {(selectedProduto as any).descricao_especifica}</span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-muted">Sem especificação</span>
                                        )}
                                    </div>
                                </div>

                                {/* Estoque Atual */}
                                <div className="mb-3">
                                    <label className="form-label">Estoque Atual</label>
                                    <div className="form-control bg-light d-flex align-items-center">
                                        {getEstoqueIcon(selectedProduto.estoque_atual || 0)}
                                        <span className="ms-2">{formatEstoque(selectedProduto.estoque_atual || 0)} {selectedProduto.unidade_medida || "UN"}</span>
                                    </div>
                                </div>

                                {/* Quantidade a Adicionar */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Quantidade a Adicionar <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="quantidade"
                                        value={formData.quantidade}
                                        onChange={handleInputChange}
                                        min="0.01"
                                        step="0.01"
                                        required
                                    />
                                </div>

                                {/* Custo Unitário */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Custo Unitário <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="custo_unitario"
                                        value={formData.custo_unitario}
                                        onChange={handleInputChange}
                                        min="0.01"
                                        step="0.01"
                                        required
                                        placeholder="0,00"
                                    />
                                </div>

                                {/* Motivo */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Motivo <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        name="motivo"
                                        value={formData.motivo}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Selecione um motivo</option>
                                        {motivosEstoque.map((motivo, index) => (
                                            <option key={index} value={motivo}>
                                                {motivo}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Observações */}
                                <div className="mb-3">
                                    <label className="form-label">Observações</label>
                                    <textarea
                                        className="form-control"
                                        name="observacoes"
                                        value={formData.observacoes}
                                        onChange={handleInputChange}
                                        rows={3}
                                        placeholder="Observações adicionais..."
                                    />
                                </div>

                                {/* Seção de Anexo de Documento */}
                                <div className="card bg-light border-0 mb-3">
                                    <div className="card-header bg-transparent border-0 pb-2">
                                        <h6 className="mb-0">
                                            <i className="ti ti-paperclip me-2"></i>
                                            Anexar Documento (Opcional)
                                        </h6>
                                    </div>
                                    <div className="card-body pt-2">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="col-form-label">
                                                        Título do Documento
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={anexoData.titulo}
                                                        onChange={(e) => setAnexoData({
                                                            ...anexoData,
                                                            titulo: e.target.value
                                                        })}
                                                        placeholder="Ex: Nota Fiscal, Comprovante"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="col-form-label">
                                                        Descrição
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={2}
                                                        value={anexoData.descricao}
                                                        onChange={(e) => setAnexoData({
                                                            ...anexoData,
                                                            descricao: e.target.value
                                                        })}
                                                        placeholder="Descrição opcional do documento"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="col-form-label">
                                                        Arquivo
                                                    </label>
                                                    <div className="drag-attach">
                                                        <input
                                                            type="file"
                                                            ref={fileInputRef}
                                                            onChange={handleFileUpload}
                                                            multiple
                                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                        />
                                                        <div className="img-upload">
                                                            <i className="ti ti-file-broken" />
                                                            Enviar Arquivo
                                                        </div>
                                                    </div>
                                                    <div className="form-text">
                                                        Formatos aceitos: PDF, JPG, PNG, DOC, DOCX (máx. 10MB)
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Arquivos selecionados */}
                                            {uploadedFiles.length > 0 && (
                                                <div className="col-md-12">
                                                    <label className="col-form-label">Arquivos Selecionados</label>
                                                    {uploadedFiles.map((fileData, index) => (
                                                        <div key={index} className="upload-file upload-list">
                                                            <div>
                                                                <h6>{fileData.name}</h6>
                                                                <p>{fileData.size}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveFile(index)}
                                                                className="text-danger border-0 bg-transparent"
                                                                type="button"
                                                            >
                                                                <i className="ti ti-trash-x" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Preview do Novo Estoque e Valor Total */}
                                {formData.quantidade > 0 && (
                                    <div className="alert alert-info">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <strong>Novo estoque:</strong> {formatEstoque((selectedProduto.estoque_atual || 0) + formData.quantidade)} {selectedProduto.unidade_medida || "UN"}
                                            </div>
                                            {formData.custo_unitario > 0 && (
                                                <div className="col-md-6">
                                                    <strong>Valor total:</strong> {formatCurrency(formData.quantidade * formData.custo_unitario)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button variant="success" type="submit" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Adicionando...
                                </>
                            ) : (
                                'Adicionar Estoque'
                            )}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>
        </>
    );
};

export default GestaoEstoqueContrato; 